import { autorun, configure } from "mobx";
import makeInspectable from "mobx-devtools-mst";
import { Provider } from "mobx-react";
import { setLivelinessChecking } from "mobx-state-tree";
import React from "react";
import ReactDOM from "react-dom";
import { RawIntlProvider } from "react-intl";
import { Redirect, Route, Router, Switch } from "react-router";
import { getMessages, intl, L10nData } from "@fidget/common/i18n";
import { SharedModelsGetter } from "@fidget/common/models/shared/storeUtils";
import {
  ArtichokeService,
  DefaultArtichokeService
} from "@fidget/common/services/artichokeService";
import { UserActivityService } from "@fidget/common/services/userActivityService";
import { allowWindowClosing, disallowWindowClosing } from "@fidget/common/services/windowUtils";
import { logger, onDOMContentLoaded, parseQueryString } from "@fidget/common/utils";
import { ServerLogger } from "@fidget/common/utils/logger/serverLogger";
import { Login } from "./components/beforeLogin/login/Login";
import { MagicLink } from "./components/beforeLogin/login/magicLink";
import { ResetPassword } from "./components/beforeLogin/login/resetPassword";
import { LoginOidcResponse } from "./components/beforeLogin/loginOidcResponse/loginOidcResponse";
import { PrivateRoute } from "./components/privateRoute/privateRoute";
import { PublicRoute } from "./components/publicRoute/publicRoute";
import { AgentDashboardContainer } from "./containers/dashboard";
import { DashboardView } from "./containers/dashboard/views/DashboardView";
import { SignUpContainer, SignUpView } from "./containers/signup";
import { RootStore, RootStoreEnv } from "./models";
import { INBOX_INITIAL_DATA } from "./models/inbox";
import { Path } from "./models/router";
import { ReportTabs } from "./models/router/utils";
import { PluginService } from "./plugins/pluginService";
import { WsGlobalEventsListener } from "@fidget/common/services/eventsListener";
import { DefaultRingtoneService, RingtoneService } from "./services/ringtoneService";
import { DefaultSpinnerService } from "./services/spinner/defaultSpinnerService";
import { getLocaleFromBrowser, getLanguageFromLocale } from "@fidget/common/services/language";
import { AgentCallService } from "./services/AgentCallService";
import { AgentServices, AgentServicesContext } from "./models/agentServices";
import { OIDCService } from "./services/OIDCService";
import { SpinnerService } from "./services/spinner/spinnerService";
import { WindowDimensionsProvider } from "./models/WindowDimensions";
import { config } from "@fidget/common/config";
import { ReportsScope } from "./models/reports/ReportsScope";

require("@formatjs/intl-pluralrules/polyfill");
require("@formatjs/intl-pluralrules/locale-data/en");
require("@formatjs/intl-pluralrules/locale-data/pl");
require("@formatjs/intl-pluralrules/locale-data/ka");
require("@formatjs/intl-pluralrules/locale-data/es");

require("@formatjs/intl-relativetimeformat/polyfill");
require("@formatjs/intl-relativetimeformat/locale-data/en");
require("@formatjs/intl-relativetimeformat/locale-data/pl");
require("@formatjs/intl-relativetimeformat/locale-data/ka");
require("@formatjs/intl-relativetimeformat/locale-data/es");

// spyOnActions();
configure({ enforceActions: "never" });

// FIXME: workaround for MST complaining about dead ChatItem reads/writes after
//   pending item is removed from the collection
setLivelinessChecking("ignore");

if (config.services.serverLoggerEnabled) {
  logger.addLogger(new ServerLogger({ app: "agent" }));
}

const spinnerService: SpinnerService = new DefaultSpinnerService();
const artichokeService: ArtichokeService = new DefaultArtichokeService();
const oidcService = new OIDCService(spinnerService);
const pluginService = PluginService.getInstance();
const eventListener = new WsGlobalEventsListener(artichokeService);
const userActivityService = new UserActivityService();
const ringtoneService: RingtoneService = new DefaultRingtoneService();
const callService = new AgentCallService(artichokeService, ringtoneService);

// create new style stores
const modelsGetter: SharedModelsGetter = {
  sharedOrgConfigGetter: () => rootStore.currentUserStore.orgConfig
};

const services: AgentServices = {
  spinnerService,
  artichokeService,
  oidcService,
  callService,
  ringtoneService
};

const rootStoreEnv: RootStoreEnv = {
  ...services,
  pluginService,
  userActivityService,
  ringtoneService,
  eventListener,
  modelsGetter
};

const rootStore = RootStore.create(
  {
    routerStore: {},
    currentUserStore: { loginRedirectPath: Path.MAIN, planPrice: 0 },
    inboxStore: INBOX_INITIAL_DATA,
    roomStore: {},
    agentStore: { agents: {} },
    calendarStore: { trash: {} },
    callStore: {},
    uiStore: {},
    intentStore: {},
    widgetStore: {},
    paymentStore: {},
    cobrowsingStore: { cobrowsingInProgress: false },
    profanityStore: {},
    reportsStore: {},
    tagsStore: {},
    tagGroupsStore: {},
    roleStore: {},
    agentGroupStore: {},
    messageWidgetsStore: {},
    topicStore: {},
    featuresStore: {},
    guestStore: {},
    usersStore: {},
    tagGroupRulesStore: {}
  },
  rootStoreEnv
);

makeInspectable(rootStore);

// FIXME: strictly provisional - needed due to convoluted logic in src/agent/store/main
//   remove as soon as relevant bits are refactored
(window as any).rootStore = rootStore;

autorun(() => {
  if (!rootStore.callStore.state) {
    allowWindowClosing();
  } else {
    disallowWindowClosing();
  }
});

const Main = () => (
  <AgentServicesContext.Provider value={services}>
    <Provider {...rootStore}>
      <WindowDimensionsProvider>
        <RawIntlProvider value={intl.intl}>
          <Router history={rootStore.routerStore.history}>
            <Switch>
              <PublicRoute exact path={Path.LOGIN} component={Login} />
              <PublicRoute exact path={Path.LOGIN_OIDC_RESPONSE} component={LoginOidcResponse} />

              <PrivateRoute
                exact
                path={`${Path.ROOM}/:id`}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.CONVERSATIONS} />}
              />
              <PrivateRoute
                exact
                path={Path.MAIN}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.CONVERSATIONS} />}
              />
              <PrivateRoute
                exact
                path={Path.CALENDAR}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.CALENDAR} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_PROFILE}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_PROFILE} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_USERS}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_USERS} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_ADVISER_GROUPS}
                redirectTo={Path.LOGIN}
                render={() => (
                  <AgentDashboardContainer view={DashboardView.SETTINGS_ADVISER_GROUPS} />
                )}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_ADVISER_STATUSES}
                redirectTo={Path.LOGIN}
                render={() => (
                  <AgentDashboardContainer view={DashboardView.SETTINGS_ADVISER_STATUSES} />
                )}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_ADVISER_AVATAR}
                redirectTo={Path.LOGIN}
                render={() => (
                  <AgentDashboardContainer view={DashboardView.SETTINGS_ADVISER_AVATAR} />
                )}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_BILLING}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_BILLING} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_SETTINGS} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_ASSIGNMENT_RULES}
                redirectTo={Path.LOGIN}
                render={() => (
                  <AgentDashboardContainer view={DashboardView.SETTINGS_ASSIGNMENT_RULES} />
                )}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_MANUAL_ASSIGNMENT}
                redirectTo={Path.LOGIN}
                render={() => (
                  <AgentDashboardContainer view={DashboardView.SETTINGS_MANUAL_ASSIGNMENT} />
                )}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_BOTS}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_BOTS} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_PROACTIVE_MESSAGES}
                redirectTo={Path.LOGIN}
                render={() => (
                  <AgentDashboardContainer view={DashboardView.SETTINGS_PROACTIVE_MESSAGES} />
                )}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_TAGGING}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_TAGGING} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_TAGS}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_TAGS} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_TAG_GROUPS}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_TAG_GROUPS} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_WIDGET}
                redirectTo={Path.LOGIN}
                render={() => (
                  <AgentDashboardContainer view={DashboardView.SETTINGS_WIDGET_CONFIG} />
                )}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_MESSAGES}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_MESSAGES} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_WORKING_HOURS}
                redirectTo={Path.LOGIN}
                render={() => (
                  <AgentDashboardContainer view={DashboardView.SETTINGS_WORKING_HOURS} />
                )}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_REPORTS}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_REPORTS} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_ROLES}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_ROLES} />}
              />
              <PrivateRoute
                exact
                path={Path.SETTINGS_WEBHOOKS}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.SETTINGS_WEBHOOKS} />}
              />
              <PrivateRoute
                exact
                path={Path.AI_SETTINGS_GENERAL}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.AI_SETTINGS_GENERAL} />}
              />
              <PrivateRoute
                exact
                path={Path.AI_SETTINGS_MODEL_CONFIGURATION}
                redirectTo={Path.LOGIN}
                render={() => (
                  <AgentDashboardContainer view={DashboardView.AI_SETTINGS_MODEL_CONFIGURATION} />
                )}
              />
              <PrivateRoute
                exact
                path={Path.AI_SETTINGS_TESTING}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.AI_SETTINGS_TESTING} />}
              />
              <PrivateRoute
                exact
                path={`${Path.REPORTS}/${ReportsScope.Yours}/${ReportTabs.Efficiency}`}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.REPORTS_EFFICIENCY} />}
              />
              <PrivateRoute
                exact
                path={`${Path.REPORTS}/${ReportsScope.Groups}/${ReportTabs.Efficiency}`}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.REPORTS_EFFICIENCY} />}
              />
              <PrivateRoute
                exact
                path={`${Path.REPORTS}/${ReportsScope.Conversations}/${ReportTabs.ServiceLevelTagGroups}`}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.REPORTS_SERVICE_LEVEL_TAG_GROUPS} />}
              />
              <PrivateRoute
                exact
                path={`${Path.REPORTS}/${ReportsScope.Conversations}/${ReportTabs.ServiceLevelTags}`}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.REPORTS_SERVICE_LEVEL_TAGS} />}
              />
              <PrivateRoute
                exact
                path={`${Path.REPORTS}/${ReportsScope.Conversations}/${ReportTabs.BackLineTags}`}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.REPORTS_BACK_LINE_TAGS} />}
              />
              <PrivateRoute
                exact
                path={`${Path.REPORTS}/${ReportsScope.Advisers}/${ReportTabs.AdviserGroups}`}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.REPORTS_ADVISER_GROUPS} />}
              />
              <PrivateRoute
                exact
                path={`${Path.REPORTS}/${ReportsScope.Advisers}/${ReportTabs.TagGroups}`}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.REPORTS_ADVISER_TAG_GROUPS} />}
              />
              <PrivateRoute
                exact
                path={`${Path.REPORTS}/${ReportsScope.Org}/${ReportTabs.Other}`}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.REPORTS_OTHER} />}
              />
              <PrivateRoute
                exact
                path={`${Path.REPORTS}/${ReportsScope.Org}/${ReportTabs.Efficiency}`}
                redirectTo={Path.LOGIN}
                render={() => <AgentDashboardContainer view={DashboardView.REPORTS_EFFICIENCY} />}
              />
              <PublicRoute path={Path.LOGIN_WITH_MAGIC_LINK} component={MagicLink} />
              <PublicRoute path={Path.PASSWORD_RESET} component={ResetPassword} />
              <PublicRoute
                path={Path.ACCEPT_INVITATION}
                redirectTo={Path.SETTINGS_PROFILE}
                render={() => <SignUpContainer view={SignUpView.ACCEPT_INVITATION} />}
              />
              <PublicRoute
                path={Path.ORG_SIGNUP}
                redirectTo={Path.SETTINGS_WIDGET}
                render={() => <SignUpContainer view={SignUpView.ORG_SIGNUP} />}
              />
              <PublicRoute
                path={Path.ORG_SIGNUP_DETAILS}
                render={() => <SignUpContainer view={SignUpView.ORG_SIGNUP_DETAILS} />}
              />
              <Route>
                <Redirect to={Path.MAIN} />
              </Route>
            </Switch>
          </Router>
        </RawIntlProvider>
      </WindowDimensionsProvider>
    </Provider>
  </AgentServicesContext.Provider>
);

onDOMContentLoaded(async () => {
  rootStore.featuresStore.processParams(parseQueryString(window.location.search.substring(1)));
  const locale = getLocaleFromBrowser();
  const messages: L10nData = await getMessages(getLanguageFromLocale(locale));
  intl.setMessages(locale, messages);
  // play init sound when page is in the foreground so it is possible to play sound in the background
  userActivityService.onFirstUserInteraction(() => ringtoneService.init());
  rootStore.currentUserStore.login().then(() => {
    return ReactDOM.render(<Main />, document.getElementById("app"));
  });
});
